import { queryOptions, useQueryClient, useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import { routeQueryKeys, Routes } from '#api';
import type { Successful } from '#dn-types/api.ts';
import type { Route } from '#dn-types/route.ts';
import { makeMaxResourceQueryFn } from '#util/api.ts';

export const makeRouteQueryOptions = (routeId: string) =>
  queryOptions({
    queryKey: routeQueryKeys.detail(routeId),
    queryFn: ({ signal }) => Routes.getRoute(routeId, { signal }),
  });

/**
 * {@link useSuspenseRouteQuery} fetches route details from the api.  We can't use list data as
 * initial data because it is a different shape
 */
export function useSuspenseRouteQuery(routeId: string) {
  return useSuspenseQuery(makeRouteQueryOptions(routeId));
}

/** We don't expect to have more than 500 routes, so let's use the maxList pattern */
export const maxRoutesQueryOptions = queryOptions({
  queryKey: routeQueryKeys.maxList,
  queryFn: makeMaxResourceQueryFn(Routes.getRoutes),
});

/** Returns the total number of routes created in account */
export const routesCountQueryOptions = queryOptions({
  ...maxRoutesQueryOptions,
  select(data) {
    return data.metadata.totalCount;
  },
});

// Creates multiple concurrent suspense requests for route details.
export function useSuspenseRoutesDetailsQueries(routeIds: Array<`route-${string}`>) {
  return useSuspenseQueries({
    queries: routeIds.map((routeId) => ({
      ...makeRouteQueryOptions(routeId),
    })),
  });
}

export function useSuspenseMaxRoutesQuery() {
  return useSuspenseQuery(maxRoutesQueryOptions);
}

export function useSuspenseRouteName(routeId: string) {
  const queryClient = useQueryClient();

  const routeQuery = useSuspenseQuery({
    ...makeRouteQueryOptions(routeId),
    // Use a route details from the routes list query as the initial data, if possible
    initialData: () => {
      const routesQueryResults = queryClient.getQueryData<Successful<Route[] | null> | undefined>(
        maxRoutesQueryOptions.queryKey
      );
      const route = routesQueryResults?.data?.find((d) => {
        return d.id === routeId;
      });

      // If we found a match, use it as the initial data for the query
      if (route) {
        return { data: route };
      }
    },
    initialDataUpdatedAt: () => queryClient.getQueryState(maxRoutesQueryOptions.queryKey)?.dataUpdatedAt,
    select({ data }) {
      return { data: data.name };
    },
  });

  return routeQuery.data.data;
}
